import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone } from 'lucide-react';
import { servicesData } from '../data/services';

export const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);

  const moreLinks = [
    { to: '/faq', label: 'FAQ', testId: 'nav-faq' },
    { to: '/knowledge-hub', label: 'Knowledge Hub', testId: 'nav-knowledge-hub' },
    { to: '/case-studies', label: 'Case Studies', testId: 'nav-case-studies' },
    { to: '/guides', label: 'Guides', testId: 'nav-guides' },
    { to: '/team', label: 'Our Team', testId: 'nav-team' },
  ];

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-border/40">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center" data-testid="header-logo">
            <img 
              src="/logos/header_logo.png" 
              alt="Asbestosurv" 
              className="h-10 w-auto object-contain"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-6">
            <Link 
              to="/" 
              className="text-sm font-medium text-secondary hover:text-primary transition-colors"
              data-testid="nav-home"
            >
              HOME
            </Link>
            
            {/* Services Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setIsServicesOpen(true)}
              onMouseLeave={() => setIsServicesOpen(false)}
            >
              <button 
                className="text-sm font-medium text-secondary hover:text-primary transition-colors flex items-center gap-1"
                data-testid="nav-services-dropdown"
              >
                SERVICES
                <ChevronDown className="h-4 w-4" />
              </button>
              
              {isServicesOpen && (
                <div className="absolute top-full left-0 mt-2 w-80 bg-white border border-border shadow-xl rounded-sm max-h-[70vh] overflow-y-auto z-[100]">
                  {servicesData.map((service) => (
                    <Link
                      key={service.slug}
                      to={`/services/${service.slug}`}
                      className="block px-6 py-3 text-sm text-secondary hover:bg-primary/5 hover:text-primary transition-colors border-b border-border last:border-b-0"
                      data-testid={`service-dropdown-${service.slug}`}
                    >
                      {service.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link 
              to="/about-asbestos" 
              className="text-sm font-medium text-secondary hover:text-primary transition-colors"
              data-testid="nav-about-asbestos"
            >
              ABOUT ASBESTOS
            </Link>
            
            <Link 
              to="/contact" 
              className="text-sm font-medium text-secondary hover:text-primary transition-colors"
              data-testid="nav-contact"
            >
              CONTACT
            </Link>

            {/* More Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setIsMoreOpen(true)}
              onMouseLeave={() => setIsMoreOpen(false)}
            >
              <button 
                className="text-sm font-medium text-secondary hover:text-primary transition-colors flex items-center gap-1"
                data-testid="nav-more-dropdown"
              >
                MORE
                <ChevronDown className="h-4 w-4" />
              </button>
              
              {isMoreOpen && (
                <div className="absolute top-full right-0 mt-2 w-48 bg-white border border-border shadow-xl rounded-sm z-[100]">
                  {moreLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className="block px-6 py-3 text-sm text-secondary hover:bg-primary/5 hover:text-primary transition-colors border-b border-border last:border-b-0"
                      data-testid={link.testId}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <a 
              href="tel:07704885084" 
              className="bg-primary text-white hover:bg-primary/90 rounded-sm px-5 py-2.5 font-semibold text-sm tracking-wide transition-colors shadow-sm hover:shadow-md flex items-center gap-2 whitespace-nowrap"
              data-testid="nav-call-button"
            >
              <Phone className="h-4 w-4" />
              07704 885084
            </a>
          </nav>

          <button
            className="lg:hidden p-2 text-secondary"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="lg:hidden py-6 border-t border-border" data-testid="mobile-menu">
            <nav className="flex flex-col gap-4">
              <Link 
                to="/" 
                className="text-sm font-medium text-secondary hover:text-primary transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid="mobile-nav-home"
              >
                HOME
              </Link>
              
              {/* Mobile Services Dropdown */}
              <div className="flex flex-col">
                <button 
                  className="text-sm font-medium text-secondary hover:text-primary transition-colors flex items-center gap-1 justify-between"
                  onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                  data-testid="mobile-services-toggle"
                >
                  SERVICES
                  <ChevronDown className={`h-4 w-4 transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`} />
                </button>
                {mobileServicesOpen && (
                  <div className="mt-2 ml-4 flex flex-col gap-2">
                    {servicesData.map((service) => (
                      <Link
                        key={service.slug}
                        to={`/services/${service.slug}`}
                        className="text-xs text-muted-foreground hover:text-primary transition-colors py-1"
                        onClick={() => setIsMobileMenuOpen(false)}
                        data-testid={`mobile-service-${service.slug}`}
                      >
                        {service.title}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link 
                to="/about-asbestos" 
                className="text-sm font-medium text-secondary hover:text-primary transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid="mobile-nav-about-asbestos"
              >
                ABOUT ASBESTOS
              </Link>
              
              <Link 
                to="/contact" 
                className="text-sm font-medium text-secondary hover:text-primary transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid="mobile-nav-contact"
              >
                CONTACT
              </Link>

              {/* Mobile More Dropdown */}
              <div className="flex flex-col">
                <button 
                  className="text-sm font-medium text-secondary hover:text-primary transition-colors flex items-center gap-1 justify-between"
                  onClick={() => setMobileMoreOpen(!mobileMoreOpen)}
                  data-testid="mobile-more-toggle"
                >
                  MORE
                  <ChevronDown className={`h-4 w-4 transition-transform ${mobileMoreOpen ? 'rotate-180' : ''}`} />
                </button>
                {mobileMoreOpen && (
                  <div className="mt-2 ml-4 flex flex-col gap-2">
                    {moreLinks.map((link) => (
                      <Link
                        key={link.to}
                        to={link.to}
                        className="text-xs text-muted-foreground hover:text-primary transition-colors py-1"
                        onClick={() => setIsMobileMenuOpen(false)}
                        data-testid={`mobile-${link.testId}`}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <a 
                href="tel:07704885084" 
                className="bg-primary text-white hover:bg-primary/90 rounded-sm px-6 py-3 font-semibold text-sm tracking-wide transition-colors shadow-sm hover:shadow-md text-center mt-2 flex items-center justify-center gap-2 w-full"
                data-testid="mobile-call-button"
              >
                <Phone className="h-4 w-4" />
                07704 885084
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
